import React, { useEffect, useMemo } from "react";
import {
  Badge,
  Col,
  List,
  Row,
  Spin,
  Tooltip,
  Typography,
} from "../utils/antd-imports/AntdImports";
import { useDispatch, useSelector } from "react-redux";
import { capitalizeFirst, truncateDescriptionText } from "../utils/Utils";
import { getChatHistory } from "../../../store/thunks/common-thunks/commonThunk";
import { useNavigate, useParams } from "react-router-dom";

const ProductChatListPage = () => {
  const { Text, Title } = Typography;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { campaignId, bookingId } = useParams();

  /* REDUX STATE */
  const { campaignProductsChatHistory, chatLoading } = useSelector(
    (state) => state.chatHistory
  );
  const { isLiveMsgNotification } = useSelector((state) => state.webSocketMsgs);
  const { clearedCampaigns, clearedProducts } = useSelector(
    (state) => state.chatCommunicationHandleSlice
  );

  /* LOAD PRODUCTS FOR CAMPAIGN */
  useEffect(() => {
    if (!campaignId) return;

    dispatch(
      getChatHistory({
        campaignId,
        bookingId: null,
      })
    );
  }, [campaignId, dispatch]);

  /* LIVE UNREAD MESSAGE STATS PER PRODUCT */
  const productLiveStats = useMemo(() => {
    if (!campaignId) return {};

    const routeCampaignId = String(campaignId);
    const activeBookingId = String(bookingId || "");

    return isLiveMsgNotification.reduce((acc, msg) => {
      const msgCampaignId = String(msg.campaignId);
      const msgBookingId = String(msg.bookingId);

      if (msgCampaignId !== routeCampaignId) return acc;

      const msgTime = new Date(msg.timestamp).getTime();

      const clearedCampaignTime = clearedCampaigns?.[routeCampaignId]
        ? new Date(clearedCampaigns[routeCampaignId]).getTime()
        : 0;
      if (msgTime <= clearedCampaignTime) return acc;

      const clearedProductTime = clearedProducts?.[routeCampaignId]?.[
        msgBookingId
      ]
        ? new Date(clearedProducts[routeCampaignId][msgBookingId]).getTime()
        : 0;
      if (msgTime <= clearedProductTime) return acc;

      /* ACTIVE PRODUCT SHOULD NOT COUNT */
      if (msgBookingId === activeBookingId) return acc;

      if (!acc[msgBookingId]) {
        acc[msgBookingId] = {
          count: 0,
          lastMessageTime: msgTime,
        };
      }

      acc[msgBookingId].count += 1;
      acc[msgBookingId].lastMessageTime = Math.max(
        acc[msgBookingId].lastMessageTime,
        msgTime
      );

      return acc;
    }, {});
  }, [
    isLiveMsgNotification,
    campaignId,
    bookingId,
    clearedCampaigns,
    clearedProducts,
  ]);

  /* MERGE PRODUCTS WITH UNREAD COUNTS */
  const mergedProducts = useMemo(() => {
    return (
      campaignProductsChatHistory?.[0]?.products?.map((product) => {
        const bookingIdStr = String(product.bookingId);
        const live = productLiveStats[bookingIdStr];

        return {
          ...product,
          unreadCount: live?.count ?? 0,
          lastMessageTime: live?.lastMessageTime ?? null,
        };
      }) || []
    );
  }, [campaignProductsChatHistory, productLiveStats]);

  /* NAVIGATION */
  const handleProductChat = (item) => {
    navigate(`/seller/products-chat-list/${campaignId}/${item.bookingId}`);
  };

  /* RENDER */
  return (
    <div style={{ backgroundColor: "#fff" }}>
      <div style={{ padding: 16 }}>
        <Title className="chat-campaign-name">
          {capitalizeFirst(campaignProductsChatHistory?.[0]?.campaignName)}
        </Title>
        <Text className="select-prodcut-sub-text">
          Select a product to view buyer conversations
        </Text>
      </div>

      <div
        style={{
          height: 550,
          overflowY: "auto",
          padding: "5px 15px",
        }}
      >
        <List
          itemLayout="horizontal"
          dataSource={mergedProducts}
          renderItem={(item) => {
            const isActive = String(item.bookingId) === String(bookingId);

            return (
              <List.Item
                style={{
                  cursor: "pointer",
                  padding: "16px 12px",
                  background: "#fff",
                }}
                onClick={() => handleProductChat(item)}
              >
                <div style={{ width: "100%" }}>
                  <Row>
                    <Col span={22}>
                      <Tooltip title={item.title}>
                        <Text className="chat-product-name">
                          {capitalizeFirst(truncateDescriptionText(item.title))}
                        </Text>
                      </Tooltip>
                    </Col>
                  </Row>

                  <Row>
                    <Col span={22}>
                      <Text className="product-buyer-name">
                        Buyer: {capitalizeFirst(item.user_full_name)}
                      </Text>
                    </Col>

                    <Col span={2} style={{ textAlign: "right" }}>
                      <Badge
                        count={isActive ? 0 : item.unreadCount}
                        style={{
                          backgroundColor: "#d2042d",
                        }}
                      />
                    </Col>
                  </Row>
                </div>
              </List.Item>
            );
          }}
        />
      </div>

      {chatLoading && <Spin fullscreen />}
    </div>
  );
};

export default ProductChatListPage;
